  import { API_BETA, jsonHeadersWithToken, forceLogout } from '@/lib/constants'

const BASE_URL = API_BETA

export const getWorkspaces = async () => {
  const res = await fetch(`${BASE_URL}workspaces`, {
    method: 'GET',
    headers: jsonHeadersWithToken(),
  })
  if (!res.ok) {
    if (res.status === 401) {
      forceLogout()
      return new Promise(() => {}) // never resolves — page is navigating away
    }
    throw new Error('Failed to fetch workspaces')
  }
  const result = await res.json()
  return result
}

export const createWorkspace = async (name) => {
  const res = await fetch(`${BASE_URL}workspaces`, {
    method: 'POST',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify({ name }),
  })
  if (!res.ok) throw new Error('Failed to create workspace')
  return res.json()
}

export const getWorkspaceDetails = async (workspaceId = 'default') => {
  const res = await fetch(`${BASE_URL}workspaces/${workspaceId}`, {
    method: 'GET',
    headers: jsonHeadersWithToken(),
  })
  if (!res.ok) throw new Error('Failed to fetch workspace details')
  return res.json()
}

//update workspace name and logo
export const updateWorkspace = async ({ workspaceId, data }) => {
  if (!workspaceId) throw new Error('workspaceId is required')
  if (!data || typeof data !== 'object') {
    throw new Error('Update data is required')
  }

  const res = await fetch(`${BASE_URL}workspaces/${workspaceId}`, {
    method: 'PUT',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify(data),
  })

  const json = await res.json().catch(() => null)

  if (!res.ok) {
    const message = json?.error || json?.message || 'Failed to update workspace'

    throw new Error(message)
  }

  return json
}

// Set workspace as default
export const setWorkspaceAsDefault = async (workspaceId) => {
  if (!workspaceId) throw new Error('workspaceId is required')
  const url = `${BASE_URL}workspaces/${workspaceId}/set-default`
  const res = await fetch(url, {
    method: 'PUT',
    headers: jsonHeadersWithToken(),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(text || 'Failed to set default workspace')
  }
  return res.json()
}

// Delete workspace
export const deleteWorkspace = async (workspaceId) => {
  if (!workspaceId) throw new Error('workspaceId is required')
  const res = await fetch(`${BASE_URL}workspaces/${workspaceId}`, {
    method: 'DELETE',
    headers: jsonHeadersWithToken(),
  })
  if (!res.ok) throw new Error('Failed to delete workspace')
  return res.json()
}

// Leave workspace
export const leaveWorkspace = async (workspaceId) => {
  if (!workspaceId) throw new Error('workspaceId is required')
  const res = await fetch(`${BASE_URL}workspaces/${workspaceId}/leave`, {
    method: 'POST',
    headers: jsonHeadersWithToken(),
  })

  const data = await res.json().catch(() => null)

  if (!res.ok) {
    const message =
      data?.error ||
      data?.message ||
      'Failed to leave workspace'
    throw new Error(message)
  }

  return data
}


export const getWorkspaceProjects = async (workspaceId) => {
  const res = await fetch(`${BASE_URL}workspaces/${workspaceId}/projects`, {
    method: 'GET',
    headers: jsonHeadersWithToken(),
  })
  if (!res.ok) throw new Error('Failed to fetch projects')
  const result = await res.json()
  const projects = Array.isArray(result.data) ? result.data : [result.data]
  return { data: projects }
}

export const createWorkspaceProject = async ({ workspaceId, name }) => {
  if (!workspaceId) throw new Error('workspaceId is required')
  const res = await fetch(`${BASE_URL}workspaces/${workspaceId}/projects`, {
    method: 'POST',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify({ name }),
  })
  if (!res.ok) throw new Error('Failed to create project')
  if (res.status === 204) return null
  return res.json()
}

export const duplicateProject = async ({ uniqueUrl, name, category }) => {
  if (!uniqueUrl) throw new Error('uniqueUrl is required')
  const params = new URLSearchParams({ project: uniqueUrl })
  const body = {}
  if (name) body.name = name
  if (category) body.category = category

  const res = await fetch(`${BASE_URL}guest/web-builder/duplicate-project?${params}`, {
    method: 'POST',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify(body),
  })

  const data = await res.json().catch(() => null)

  if (!res.ok) {
    const message = data?.errorMessage || data?.message || data?.error || 'Failed to duplicate project'
    throw new Error(message)
  }

  return data
}

export const moveProject = async ({ projectId, destinationWorkspaceId }) => {
  if (!projectId) throw new Error('projectId is required')
  if (!destinationWorkspaceId) throw new Error('destinationWorkspaceId is required')

  const res = await fetch(`${BASE_URL}guest/web-builder/move-project`, {
    method: 'POST',
    headers: jsonHeadersWithToken(),
    body: JSON.stringify({ projectId, destinationWorkspaceId }),
  })

  const data = await res.json().catch(() => null)

  if (!res.ok) {
    const message = data?.errorMessage || data?.message || data?.error || 'Failed to move project'
    throw new Error(message)
  }

  return data
}


export const uploadWorkspaceLogo = async (file, workspaceId) => {
  if (!file) throw new Error('File is required')
  if (!workspaceId) throw new Error('workspaceId is required')

  const formData = new FormData()
  formData.append('file', file)
  formData.append('workspaceId', workspaceId)

  const tokenHeaders = jsonHeadersWithToken()
  const headers = {}
  if (tokenHeaders['x-access-token']) {
    headers['x-access-token'] = tokenHeaders['x-access-token']
  }
  if (tokenHeaders['guest-token']) {
    headers['guest-token'] = tokenHeaders['guest-token']
  }

  const res = await fetch(`${BASE_URL}guest/supabase-assets-v2/upload-workspace-logo`, {
    method: 'POST',
    headers,
    body: formData,
  })

  let data = null
  try {
    const contentType = res.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      data = await res.json()
    } else {
      const text = await res.text()
      try {
        data = JSON.parse(text)
      } catch {
        data = { error: text || 'Failed to upload workspace logo' }
      }
    }
  } catch (e) {
    data = { error: e.message || 'Failed to parse response' }
  }

  if (!res.ok) {
    const message = data?.error || data?.message || 'Failed to upload workspace logo'
    throw new Error(message)
  }

  return data
}

export const getSharedProjects = async ({ page = 1, search = '' } = {}) => {
  const params = new URLSearchParams({ page: String(page) })
  if (search && search.length >= 2) {
    params.set('search', search)
  }

  const res = await fetch(`${BASE_URL}guest/web-builder/shared?${params}`, {
    method: 'GET',
    headers: jsonHeadersWithToken(),
  })

  if (!res.ok) throw new Error('Failed to fetch shared projects')
  const result = await res.json()
  return result
}

/**
 * Fetch workspaces for a specific user (admin-only).
 * The backend verifies admin status from the JWT — no special headers needed.
 */
export const getWorkspacesForUser = async (userId) => {
  if (!userId) throw new Error('userId is required')
  const res = await fetch(`${BASE_URL}workspaces?userId=${encodeURIComponent(userId)}`, {
    method: 'GET',
    headers: jsonHeadersWithToken(),
  })
  if (!res.ok) {
    if (res.status === 401) {
      forceLogout()
      return new Promise(() => {})
    }
    if (res.status === 403) throw new Error('Admin access required')
    throw new Error('Failed to fetch workspaces for user')
  }
  return res.json()
}

export const getSharedProjectsCount = async () => {
  const res = await fetch(`${BASE_URL}guest/web-builder/shared/count`, {
    method: 'GET',
    headers: jsonHeadersWithToken(),
  })

  if (!res.ok) throw new Error('Failed to fetch shared projects count')
  const result = await res.json()
  return result
}

